import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useImportDistributorUsersMutation } from 'api/main';
import { getFileExtension, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    FileErrorMessage,
    FileInputButton,
    SuccessMessage,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';

const ImportDialog = ({ open, onClose }) => {
    const { id } = useParams();
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importSuccessful, setImportSuccessful] = useState(false);
    const [importDistributorUsers, { isSuccess, isLoading }] = useImportDistributorUsersMutation();

    const handleFileChange = useCallback(e => {
        const {
            files: { 0: file },
        } = e.target;
        if (validateFileSize(file)) {
            const extention = getFileExtension(file);
            if (extention === 'xls' || extention === 'csv' || extention === 'xlsx') {
                setSelectedFile(file);
            } else {
                setFileTypeError(true);
            }
        } else {
            setFileSizeError(true);
        }
    }, []);

    const handleConfirm = useCallback(() => {
        const formData = new FormData();
        formData.set('file', selectedFile);
        importDistributorUsers({ id, file: formData });
    }, [selectedFile, importDistributorUsers, id]);

    useEffect(() => {
        if (isSuccess) setImportSuccessful(true);
    }, [isSuccess]);

    useEffect(() => {
        if (open) {
            setImportSuccessful(false);
            setSelectedFile(null);
        }
    }, [open]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.IMPORT_USER_LIST} handleClose={onClose} />
            <DialogContent>
                <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" gutterBottom>
                        {selectedFile?.name || texts.AUCUN}
                    </Typography>
                    <FileInputButton
                        showIcon
                        id="import-spreadsheet"
                        acceptedFileTypes=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleFileChange}
                    />
                </Box>
                {fileTypeError && <FileErrorMessage variant="type" />}
                {fileSizeError && <FileErrorMessage variant="size" />}
                {importSuccessful && (
                    <SuccessMessage sx={{ marginTop: 2 }} message={texts.IMPORT_SUCCESSFUL} />
                )}
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>{texts.CANCEL}</CancelButton>
                <ActionButton
                    variant="contained"
                    loading={isLoading}
                    disabled={isLoading || !selectedFile}
                    onClick={handleConfirm}
                >
                    {texts.IMPORT}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
